/**
 * AgencyTrialBanner — persistent banner on the AgencyDashboard summarising
 * where the institution stands in the 7-day trial → annual paid lifecycle.
 *
 * Rendered states:
 *   - plan_type='pending'  → "License under review" (blocking, agency inactive)
 *   - plan_type='trial'    → countdown + Share-feedback CTA + annual-plan pricing card
 *   - plan_type='annual'   → paid-until date
 *   - plan_type='expired'  → red banner: seats paused, pay to resume
 *   - plan_type='monthly'  → "Switch to annual" nudge (5b — offer upgrade path)
 *
 * All CTAs are advisory — the actual annual activation is a super-admin toggle.
 * This banner just tells the broker what's next.
 */
import { useMemo, useState } from "react";
import { AlertTriangle, Sparkles, ShieldCheck, MessageSquareHeart, CalendarClock, Landmark, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BANK, COMPANY, PRICING_ANNUAL } from "@/data/company";

const CMA_ANNUAL = PRICING_ANNUAL.cma_kes;
const MOE_ANNUAL = PRICING_ANNUAL.moe_kes;

function daysLeft(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}

/** Company bank block — used inside the trial (urgent), expired, and monthly banners. */
function BankDetailsBlock({ annualKes, isMoE, testId }) {
    async function copyAcc() {
        try {
            await navigator.clipboard.writeText(BANK.account_number);
            toast.success("Account number copied");
        } catch { toast.error("Copy failed"); }
    }
    return (
        <div className="rounded-lg border border-border bg-background/70 p-3 text-xs leading-relaxed space-y-1.5"
             data-testid={testId || "trial-bank-block"}>
            <div className="flex items-center gap-1.5 font-extrabold text-foreground">
                <Landmark className="w-4 h-4 text-primary" />
                <span>Pay {isMoE ? "MoE" : "CMA"} annual — KES {annualKes.toLocaleString()}</span>
            </div>
            <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 font-mono">
                <span className="text-muted-foreground">Bank</span><span>{BANK.bank_name}</span>
                <span className="text-muted-foreground">Account name</span><span>{BANK.account_name}</span>
                <span className="text-muted-foreground">Account no.</span>
                <span className="flex items-center gap-2">
                    {BANK.account_number}
                    <button
                        type="button"
                        className="text-primary hover:underline inline-flex items-center gap-0.5"
                        onClick={copyAcc}
                        data-testid="trial-bank-copy-acc"
                    ><Copy className="w-3 h-3" /> copy</button>
                </span>
                <span className="text-muted-foreground">Branch</span><span>{BANK.branch}</span>
                <span className="text-muted-foreground">Branch addr.</span><span>{BANK.branch_address}</span>
                <span className="text-muted-foreground">Reference</span><span>Institution name + licence no.</span>
            </div>
            <div className="pt-1 text-muted-foreground">
                Then email proof to{" "}
                <a href={`mailto:${COMPANY.email_billing}`} className="text-primary underline">
                    {COMPANY.email_billing}
                </a>{" "}— super-admin activates within 1 working day.
            </div>
        </div>
    );
}

export default function AgencyTrialBanner({ agency, onReload }) {
    const [pollOpen, setPollOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const plan = agency?.plan_type || "pending";
    const isMoE = agency?.institution_type === "moe";
    const annualKes = isMoE ? MOE_ANNUAL : CMA_ANNUAL;

    const trialDaysLeft = useMemo(() => daysLeft(agency?.trial_ends_at), [agency?.trial_ends_at]);
    const annualDaysLeft = useMemo(() => daysLeft(agency?.annual_valid_until), [agency?.annual_valid_until]);
    const hasSubmittedPoll = !!agency?.trial_satisfaction;

    async function submitPoll() {
        if (rating < 1) return toast.error("Give us a rating first");
        setBusy(true);
        try {
            await api.post("/agency/trial-satisfaction", { rating, comment });
            toast.success("Thanks for the feedback — super-admin will review it");
            setPollOpen(false);
            setRating(0); setComment("");
            onReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save feedback");
        } finally {
            setBusy(false);
        }
    }

    if (plan === "pending") {
        return (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="trial-banner-pending">
                <ShieldCheck className="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />
                <div>
                    <div className="text-sm font-semibold text-amber-500">License under review</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Upload your {isMoE ? "MoE certificate" : "CMA license"} and our super-admin will verify it within
                        1 working day. Your 7-day free trial starts the moment you're verified.
                    </div>
                </div>
            </div>
        );
    }

    if (plan === "trial") {
        const urgent = trialDaysLeft !== null && trialDaysLeft <= 10;
        return (
            <div
                className={`rounded-xl border p-4 md:p-5 space-y-3 ${
                    urgent
                        ? "border-red-500/50 bg-red-500/5"
                        : "border-primary/40 bg-primary/5"
                }`}
                data-testid="trial-banner-trial"
            >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Sparkles className={`w-5 h-5 ${urgent ? "text-red-500" : "text-primary"}`} />
                        <div>
                            <div className="text-sm font-extrabold uppercase tracking-widest">
                                Free trial · {trialDaysLeft ?? "?"} day{trialDaysLeft === 1 ? "" : "s"} left
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                All features unlocked · trial ends {fmtDate(agency?.trial_ends_at)}
                            </div>
                        </div>
                    </div>
                    {!hasSubmittedPoll && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPollOpen(true)}
                            data-testid="trial-share-feedback"
                        >
                            <MessageSquareHeart className="w-4 h-4 mr-1.5" /> Share feedback
                        </Button>
                    )}
                </div>

                {urgent && (
                    <BankDetailsBlock annualKes={annualKes} isMoE={isMoE} testId="trial-urgent-bank" />
                )}

                {pollOpen && (
                    <div className="rounded-lg border border-border bg-background/80 p-3 space-y-2" data-testid="trial-poll">
                        <div className="text-xs font-bold uppercase tracking-widest">How's your trial going?</div>
                        <div className="flex items-center gap-1.5">
                            {[1,2,3,4,5].map((n) => (
                                <button
                                    key={n}
                                    onClick={() => setRating(n)}
                                    className={`w-8 h-8 rounded border text-sm font-bold ${
                                        n <= rating
                                            ? "border-primary bg-primary text-primary-foreground"
                                            : "border-border hover:border-primary/50"
                                    }`}
                                    data-testid={`trial-poll-star-${n}`}
                                    type="button"
                                >{n}</button>
                            ))}
                        </div>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Optional — anything specific working well, or breaking?"
                            className="w-full min-h-[70px] rounded border border-border bg-background p-2 text-sm"
                            data-testid="trial-poll-comment"
                        />
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setPollOpen(false)}>Cancel</Button>
                            <Button size="sm" onClick={submitPoll} disabled={busy || rating < 1} data-testid="trial-poll-submit">
                                Submit feedback
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (plan === "annual") {
        return (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="trial-banner-annual">
                <CalendarClock className="w-5 h-5 shrink-0 text-emerald-500" />
                <div className="flex-1">
                    <div className="text-sm font-semibold">Annual plan active</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Valid until {fmtDate(agency?.annual_valid_until)}
                        {annualDaysLeft !== null && ` · ${annualDaysLeft} days remaining`}
                        {agency?.annual_amount_paid_kes && ` · KES ${(+agency.annual_amount_paid_kes).toLocaleString()} paid`}
                    </div>
                </div>
            </div>
        );
    }

    if (plan === "expired") {
        return (
            <div className="rounded-lg border-2 border-red-500/60 bg-red-500/10 p-4 space-y-3" data-testid="trial-banner-expired">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 shrink-0 text-red-500 mt-0.5" />
                    <div>
                        <div className="text-sm font-extrabold text-red-500">Trial ended · new-seat onboarding paused</div>
                        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            Existing traders retain read-only access to their positions. To resume onboarding new
                            seats, wire the annual subscription below.
                        </div>
                    </div>
                </div>
                <BankDetailsBlock annualKes={annualKes} isMoE={isMoE} testId="trial-expired-bank" />
            </div>
        );
    }

    if (plan === "monthly") {
        return (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="trial-banner-monthly">
                <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 shrink-0 text-primary" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold">Save with the annual plan</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Switch from monthly to a single yearly payment (below) — super-admin will activate.
                        </div>
                    </div>
                </div>
                <BankDetailsBlock annualKes={annualKes} isMoE={isMoE} testId="trial-monthly-bank" />
            </div>
        );
    }

    return null;
}
